import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Sentry — project recotex/calculaties-web (de.sentry.io). De DSN is publiek (zit toch in de
// bundle); VITE_SENTRY_DSN overschrijft hem. Enkel actief in de productiebuild.
const DEFAULT_DSN = 'https://f855fcee4af77a4cfb2c3b9c43f629d2@o4512256286720000.ingest.de.sentry.io/4512294012715088';

// Toolbox SSO-token (/?toolbox_token=<jwt>) en OAuth-codes nooit doorsturen.
const scrubUrl = (u?: string) => u
  ?.replace(/([?&](?:toolbox_token|token|code|state|secret)=)[^&#]*/gi, '$1[Filtered]')
  .replace(/(verify-token\/)[^/?#]+/g, '$1[Filtered]');

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || DEFAULT_DSN,
  enabled: import.meta.env.PROD,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({ useEffect, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes }),
    // Replay enkel bij een fout; alle tekst en invoer gemaskeerd (prijzen, onderaannemers, klantnamen).
    Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
  ],
  tracesSampleRate: 0.2,
  // Geen cookies, bodies of headers (prijzen/klantnamen); gebruiker zetten we zelf na /api/auth/me.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    urlQueryParams: { deny: ['toolbox_token', 'token', 'code', 'state', 'secret', 'q', 'search'] }, // q/search = zoekterm (klantnamen)
  },
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: [
    // API-fouten die al als melding in de UI verschijnen en server-side in Sentry staan
    /^API (401|403|404|429)$/,
    // bewuste throw in main.tsx tijdens de SSO-redirect
    'redirecting to SSO',
    'ResizeObserver loop',
    // netwerkfouten (offline, tab gesloten) — geen bug in de app
    'Failed to fetch',
    'Load failed',
  ],
  beforeSend(event) {
    if (event.request?.url) event.request.url = scrubUrl(event.request.url);
    for (const b of event.breadcrumbs || []) {
      if (typeof b.data?.url === 'string') b.data.url = scrubUrl(b.data.url);
      if (typeof b.data?.to === 'string') b.data.to = scrubUrl(b.data.to);
      if (typeof b.data?.from === 'string') b.data.from = scrubUrl(b.data.from);
    }
    return event;
  },
  // v11 streamt spans: URL-attributen en spannamen door dezelfde filter.
  beforeSendSpan(span) {
    span.name = scrubUrl(span.name) ?? span.name;
    const attrs = span.attributes as Record<string, unknown>;
    for (const [k, v] of Object.entries(attrs || {})) {
      if (typeof v === 'string') attrs[k] = scrubUrl(v);
      else if (v && typeof (v as { value?: unknown }).value === 'string') (v as { value: string }).value = scrubUrl((v as { value: string }).value)!;
    }
    return span;
  },
});

export { Sentry };
